<template>
    <div class="stl-run-wrap">
        <div class="stl-run-scroll">
            <div class="stl-run-card">
                <div class="stl-run-card-head">
                    <span class="stl-run-card-title">멀티 에너지 일괄 정산</span>
                    <span class="stl-run-card-sub">{{ ym }} · 활성 {{ energies.length }}종</span>
                </div>
                <div class="stl-run-card-body">
                    <p class="stl-hint" style="margin-bottom: 14px;">
                        에너지별 총요금을 입력한 뒤 <strong>전체 미리보기</strong> → <strong>전체 임시저장</strong> →
                        <strong>전체 확정</strong> 순서로 진행합니다.
                    </p>

                    <div v-for="e in energies" :key="e" class="stl-bundle-energy-card">
                        <div class="stl-bundle-energy-head">
                            <span class="stl-bundle-energy-label">{{ energyLabel(e) }}</span>
                            <span v-if="statusMap[e]" :class="statusClass(statusMap[e])">{{ statusLabel(statusMap[e]) }}</span>
                            <span v-else class="stl-chip plain">미실행</span>
                        </div>
                        <KepcoBillPanel
                            v-if="e === 'Elec'"
                            v-model="items.Elec"
                            :site="site"
                            :reg-ym="regYm"
                            :disabled="isFinal(e)"
                        />
                        <div v-else class="stl-field">
                            <label>총요금 (원)</label>
                            <AmountKinput v-model="items[e].kepcoTotalAmount" :disabled="isFinal(e)" />
                        </div>
                        <div v-if="previewMap[e]" class="stl-bundle-preview">
                            <span>{{ previewMap[e].details?.length || 0 }}세대</span>
                            <span>·</span>
                            <span>배분 {{ fmtWon(previewMap[e].kepcoTotalAmount) }}원</span>
                            <span v-if="previewMap[e].floorRemainder != null">· 잔차 {{ fmtWon(previewMap[e].floorRemainder) }}원</span>
                        </div>
                        <div v-if="errorMap[e]" class="stl-banner stl-banner-danger" style="margin-top: 8px;">
                            {{ errorMap[e] }}
                        </div>
                    </div>

                    <div class="stl-flow-cta-row" style="margin-top: 16px;">
                        <button
                            type="button"
                            class="stl-btn stl-btn-primary"
                            :disabled="busy || !canPreview"
                            @click="doBundle('preview')"
                        >
                            <b-spinner v-if="busy && action === 'preview'" small />
                            <span v-else>전체 미리보기</span>
                        </button>
                        <button
                            type="button"
                            class="stl-btn"
                            :disabled="busy || !canSimulate"
                            @click="doBundle('simulate')"
                        >
                            <b-spinner v-if="busy && action === 'simulate'" small />
                            <span v-else>전체 임시저장</span>
                        </button>
                        <button
                            type="button"
                            class="stl-btn stl-btn-final"
                            :disabled="busy || !canConfirm"
                            @click="doBundle('confirm')"
                        >
                            <b-spinner v-if="busy && action === 'confirm'" small />
                            <span v-else>전체 확정</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import backEndApi from "@api/backEndApi.js";
import AmountKinput from "./AmountKinput.vue";
import KepcoBillPanel from "./KepcoBillPanel.vue";
import { fmtWon, ymToRegYm, statusLabel, statusClass, parseApiError } from "../utils/settlementFormat.js";

function defaultItem() {
    return { kepcoSource: "MANUAL", kepcoTotalAmount: null };
}

export default {
    name: "SettlementRunBundle",
    components: { AmountKinput, KepcoBillPanel },
    props: {
        site: { type: Object, required: true },
        ym: { type: String, required: true },
    },
    data() {
        return {
            items: { Elec: defaultItem(), Gas: defaultItem(), Water: defaultItem() },
            statusMap: {},
            previewMap: {},
            errorMap: {},
            busy: false,
            action: null,
        };
    },
    computed: {
        regYm() {
            return ymToRegYm(this.ym);
        },
        energies() {
            const list = [];
            if (this.site?.elecEnabled === "Y") list.push("Elec");
            if (this.site?.gasEnabled === "Y") list.push("Gas");
            if (this.site?.waterEnabled === "Y") list.push("Water");
            return list;
        },
        canPreview() {
            return this.energies.some((e) => !this.isFinal(e) && this.kReady(e));
        },
        canSimulate() {
            return this.energies.some((e) => !this.isFinal(e) && this.previewMap[e]);
        },
        canConfirm() {
            return this.energies.some((e) => this.statusMap[e] === "DRAFT");
        },
    },
    watch: {
        ym: { handler: "refresh" },
        site: { handler: "refresh", deep: true },
    },
    mounted() {
        this.refresh();
    },
    methods: {
        fmtWon,
        statusLabel,
        statusClass,
        energyLabel(e) {
            if (e === "Gas") return "가스";
            if (e === "Water") return "수도";
            return "전기";
        },
        isFinal(e) {
            return this.statusMap[e] === "FINAL";
        },
        kReady(e) {
            const v = this.items[e]?.kepcoTotalAmount;
            return v !== null && v !== "" && !this.isFinal(e);
        },
        buildPayload(action) {
            return {
                siteIdx: this.site.siteIdx,
                regYm: this.regYm,
                action,
                items: this.energies.map((e) => ({
                    energySource: e,
                    kepcoTotalAmount: this.items[e].kepcoTotalAmount,
                    kepcoSource: this.items[e].kepcoSource || "MANUAL",
                })),
            };
        },
        async refresh() {
            this.errorMap = {};
            await Promise.all(this.energies.map((e) => this.loadExisting(e)));
            this.$emit("status-change", this.overallStatus());
        },
        overallStatus() {
            const statuses = this.energies.map((e) => this.statusMap[e]).filter(Boolean);
            if (statuses.every((s) => s === "FINAL")) return "FINAL";
            if (statuses.some((s) => s === "DRAFT")) return "DRAFT";
            if (Object.keys(this.previewMap).length) return "PREVIEW";
            return null;
        },
        async loadExisting(energySource) {
            try {
                const { data } = await backEndApi.settlement.getSettlementList({
                    siteIdx: this.site.siteIdx,
                    regYm: this.regYm,
                    energySource,
                    status: "ALL",
                });
                const current = (data || [])[0];
                this.$set(this.statusMap, energySource, current?.status || null);
                if (current?.kepcoTotalAmount != null) {
                    this.$set(this.items, energySource, {
                        kepcoSource: current.kepcoSource || "MANUAL",
                        kepcoTotalAmount: Number(current.kepcoTotalAmount),
                    });
                }
            } catch (err) {
                this.$set(this.errorMap, energySource, parseApiError(err).message);
            }
        },
        async doBundle(action) {
            this.busy = true;
            this.action = action;
            this.errorMap = {};
            try {
                const { data } = await backEndApi.settlement.runBundle(this.buildPayload(action));
                for (const r of data.results || []) {
                    if (r.ok) {
                        if (action === "preview") {
                            this.$set(this.previewMap, r.energySource, r);
                        }
                        if (r.status) this.$set(this.statusMap, r.energySource, r.status);
                    } else if (!r.skipped) {
                        this.$set(this.errorMap, r.energySource, r.error || r.reason);
                    }
                }
                if (data.errors?.length) {
                    for (const e of data.errors) {
                        this.$set(this.errorMap, e.energySource, e.message);
                    }
                }
                if (action !== "preview") {
                    await this.refresh();
                }
                if (data.success) {
                    this.$bvToast.toast(
                        action === "confirm" ? "전체 확정이 완료되었습니다." : "일괄 처리가 완료되었습니다.",
                        { variant: "success", solid: true, autoHideDelay: 2500 },
                    );
                    if (action === "confirm") this.$emit("settled");
                }
                this.$emit("status-change", this.overallStatus());
            } catch (err) {
                const e = parseApiError(err);
                this.$bvToast.toast(e.message, { variant: "danger", solid: true });
            } finally {
                this.busy = false;
                this.action = null;
            }
        },
    },
};
</script>

<style scoped>
.stl-bundle-energy-card {
    border: 1px solid #e6e9ef;
    border-radius: 10px;
    padding: 14px;
    margin-bottom: 12px;
    background: #fafbfc;
}
.stl-bundle-energy-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
}
.stl-bundle-energy-label {
    font-weight: 700;
    font-size: 14px;
    color: #1a1f27;
}
.stl-bundle-preview {
    margin-top: 8px;
    font-size: 12px;
    color: #7e8798;
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}
</style>
